import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { SignalMesh } from "./SignalMesh";
import { PdfExport } from "./PdfExport";
import {
  downloadPdfBlob,
  markReportPdfError,
  markReportPdfReady,
  waitForReportPdfReady,
} from "./pdfReady";
import "./report-typography.css";
import "./pdf-pages.css";
import "./pdf-print.css";
import bgLogo from "../../assets/bg.svg";
import { Activity } from "lucide-react";
import type { AssessmentData, ReportCopy, ReportPdfModel } from "./reportTypes";
import {
  AboutGowitek,
  Framework,
  Interpretation,
  Matrix,
  NextNinety,
  Opportunities,
  Outcomes,
  Positioning,
  Risks,
  Roadmap,
  SignalBreakdown,
  SignalInputs,
  Snapshot,
} from "./reportSections";

const STORAGE_KEY = "signalai_assessment_data";
const REPORT_COPY_STORAGE_KEY = "signalai_report_copy";

type RouteState = {
  assessmentData?: AssessmentData;
  assessment_data?: AssessmentData;
  reportCopy?: ReportCopy;
  report_copy?: ReportCopy;
  assessment_id?: number | string;
};

const DEFAULT_DATA: AssessmentData = {
  company_name: "Signal AI",
  overall_score: 86,
  readiness_level: "High",
  pillar_scores: {
    strategy: 86,
    data: 80,
    technology: 84,
    process_maturity: 88,
    people: 90,
  },
};

const AREA_LABELS: Record<string, string> = {
  process_maturity: "Process automation",
  strategy: "Strategic planning",
  data: "Data management",
  technology: "Technology infrastructure",
  people: "Workforce capability",
};

const PILLAR_LABELS: Record<string, string> = {
  process_maturity: "Operational maturity",
  strategy: "Leadership alignment",
  data: "Data foundation",
  technology: "Cloud readiness",
  people: "AI execution openness",
};

const PILLAR_KEY_ALIASES: Record<string, string[]> = {
  data: ["data", "data_readiness"],
  process: ["process", "process_maturity", "process_signal"],
  people: ["people", "people_enablement", "people_signal"],
  technology: ["technology", "technology_enablement", "technology_signal"],
  strategy: ["strategy", "strategy_signal"],
};

export default function ReportPage() {
  return <Report />;
}

function Report() {
  const location = useLocation();
  const state = (location.state || {}) as RouteState;
  const [remoteAssessmentData, setRemoteAssessmentData] = useState<AssessmentData | undefined>(undefined);
  const [remoteReportCopy, setRemoteReportCopy] = useState<ReportCopy | undefined>(undefined);
  const [payloadLoaded, setPayloadLoaded] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [pdfReadySignaled, setPdfReadySignaled] = useState(false);

  const queryAssessmentId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("assessment_id");
  }, [location.search]);

  const isPdfExport = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("autopdf") === "1";
  }, [location.search]);

  useEffect(() => {
    const hasRoutePayload = Boolean(state.assessmentData || state.assessment_data || state.reportCopy || state.report_copy);
    if (!queryAssessmentId || hasRoutePayload) {
      setPayloadLoaded(true);
      return;
    }

    let isActive = true;
    setPayloadLoaded(false);

    fetch(`/api/report-payload/${queryAssessmentId}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Unable to load report payload");
        }
        return res.json();
      })
      .then((data) => {
        if (!isActive || !data || typeof data !== "object") {
          return;
        }

        const incomingAssessment = data.assessment_data as AssessmentData | undefined;
        const incomingReportCopy = data.report_copy as ReportCopy | undefined;

        if (incomingAssessment) {
          setRemoteAssessmentData(incomingAssessment);
          if (typeof window !== "undefined") {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(incomingAssessment));
          }
        }
        if (incomingReportCopy) {
          setRemoteReportCopy(incomingReportCopy);
          if (typeof window !== "undefined") {
            window.sessionStorage.setItem(REPORT_COPY_STORAGE_KEY, JSON.stringify(incomingReportCopy));
          }
        }
      })
      .catch(() => {
        // Keep existing fallback behavior.
      })
      .finally(() => {
        if (isActive) {
          setPayloadLoaded(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [queryAssessmentId, state.assessmentData, state.assessment_data, state.reportCopy, state.report_copy]);

  const assessmentData = useMemo(() => {
    const routeData = state.assessmentData || state.assessment_data;

    if (routeData) {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(routeData));
      }
      return routeData;
    }

    if (remoteAssessmentData) {
      return remoteAssessmentData;
    }

    if (typeof window !== "undefined") {
      try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored) as AssessmentData;
        }
      } catch (_error) {
        // Ignore invalid session data and fall through to defaults.
      }
    }

    return DEFAULT_DATA;
  }, [state.assessmentData, state.assessment_data, remoteAssessmentData]);

  const reportCopy = useMemo(() => {
    const routeReportCopy = state.reportCopy || state.report_copy;

    if (routeReportCopy) {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(REPORT_COPY_STORAGE_KEY, JSON.stringify(routeReportCopy));
      }
      return routeReportCopy;
    }

    if (remoteReportCopy) {
      return remoteReportCopy;
    }

    if (typeof window !== "undefined") {
      try {
        const stored = window.sessionStorage.getItem(REPORT_COPY_STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored) as ReportCopy;
        }
      } catch (_error) {
        // Ignore invalid cached report copy.
      }
    }

    return undefined;
  }, [state.reportCopy, state.report_copy, remoteReportCopy]);

  const overallScore = Math.round(assessmentData.overall_score ?? 0) || 0;
  const readinessLevel = (assessmentData.readiness_level || inferReadinessLevel(overallScore)).trim();
  const pillarScores = assessmentData.pillar_scores || DEFAULT_DATA.pillar_scores || {};
  const sortedPillars = Object.entries(pillarScores).sort((a, b) => b[1] - a[1]);
  const strongestPillar = sortedPillars[0]?.[0] || "process_maturity";
  const snapshotCopy = reportCopy?.executive_signal_snapshot;

  const snapshotData = useMemo(
    () => ({
      readiness: snapshotCopy?.readiness || "",
      fastestRoiArea: snapshotCopy?.fastest_roi_area || "",
      pilotReadiness: snapshotCopy?.pilot_readiness || "",
      opportunityWindow: snapshotCopy?.opportunity_window || "",
      executivePhrase: normalizeExecutivePhrase(snapshotCopy?.interpretation || ""),
      benchmarkCopy: snapshotCopy?.benchmark || "",
      bulletSignals: snapshotCopy?.signal_bullets || [],
    }),
    [snapshotCopy],
  );

  const breakdownScores = useMemo(
    () => ({
      data: resolvePillarScore(pillarScores, PILLAR_KEY_ALIASES.data, 70),
      process: resolvePillarScore(pillarScores, PILLAR_KEY_ALIASES.process, 70),
      people: resolvePillarScore(pillarScores, PILLAR_KEY_ALIASES.people, 100),
      technology: resolvePillarScore(pillarScores, PILLAR_KEY_ALIASES.technology, 100),
      strategy: resolvePillarScore(pillarScores, PILLAR_KEY_ALIASES.strategy, 100),
    }),
    [pillarScores],
  );

  const breakdownNotes = useMemo(
    () => ({
      data: reportCopy?.signal_breakdown?.data?.description || "Data readiness is mixed, with visibility gaps still affecting decision quality.",
      process: reportCopy?.signal_breakdown?.process?.description || "Process consistency is improving, but automation depth is not yet uniform.",
      people: reportCopy?.signal_breakdown?.people?.description || "Team alignment is positive, though capability depth varies across functions.",
      technology: reportCopy?.signal_breakdown?.technology?.description || "Technology foundations are progressing, with integration maturity still uneven.",
      strategy: reportCopy?.signal_breakdown?.strategy?.description || "Strategic intent is present, but clearer sequencing would accelerate execution.",
    }),
    [reportCopy?.signal_breakdown],
  );

  const opportunityCards = reportCopy?.top_ai_opportunities?.cards;
  const roiPrioritization = reportCopy?.roi_prioritization;
  const expectedOutcomes = reportCopy?.expected_outcomes;
  const industryPositioning = reportCopy?.industry_positioning;
  const strategicRisks = reportCopy?.strategic_risks?.items;
  const assessmentSignalInputs = reportCopy?.assessment_signal_inputs;
  const recommendedImmediateActions = reportCopy?.recommended_immediate_actions;
  const executionPlan = reportCopy?.execution_plan?.phases;

  const reportPdfModel = useMemo((): ReportPdfModel => {
    return {
      companyName: assessmentData.company_name,
      overallScore: overallScore || 86,
      snapshot: snapshotData,
      breakdownScores,
      breakdownNotes,
      interpretationCopy: reportCopy?.executive_interpretation,
      opportunityCards,
      roiPrioritization,
      executionPhases: executionPlan,
      expectedOutcomes,
      industryPositioning,
      strategicRisks,
      recommendedImmediateActions,
      assessmentSignalInputs,
    };
  }, [
    assessmentData.company_name,
    overallScore,
    snapshotData,
    breakdownScores,
    breakdownNotes,
    reportCopy?.executive_interpretation,
    opportunityCards,
    roiPrioritization,
    executionPlan,
    expectedOutcomes,
    industryPositioning,
    strategicRisks,
    recommendedImmediateActions,
    assessmentSignalInputs,
  ]);

  const isReportReady = useMemo(() => {
    if (isPdfExport && queryAssessmentId) {
      return payloadLoaded && Boolean(assessmentData);
    }
    return Boolean(assessmentData);
  }, [assessmentData, isPdfExport, payloadLoaded, queryAssessmentId]);

  const resolvedAssessmentId = useMemo(() => {
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    const routeId = state.assessment_id;
    return routeId != null ? String(routeId) : null;
  }, [queryAssessmentId, state.assessment_id]);

  const handleDownloadPdf = useCallback(async () => {
    if (!isReportReady || isExportingPdf) {
      return;
    }

    if (!resolvedAssessmentId) {
      setExportError("Assessment ID is required to download the PDF report.");
      return;
    }

    setIsExportingPdf(true);
    setExportError(null);

    try {
      const response = await fetch(`/api/report-pdf/${resolvedAssessmentId}`);
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || "Unable to generate PDF");
      }

      const blob = await response.blob();
      downloadPdfBlob(blob);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "Unable to generate PDF");
    } finally {
      setIsExportingPdf(false);
    }
  }, [isExportingPdf, isReportReady, resolvedAssessmentId]);

  useEffect(() => {
    if (!isPdfExport || !isReportReady) {
      setPdfReadySignaled(false);
      document.documentElement.removeAttribute("data-pdf-ready");
      return;
    }

    let cancelled = false;
    setPdfReadySignaled(false);

    const signalReady = async () => {
      try {
        await new Promise<void>((resolve) => setTimeout(resolve, 100));
        await waitForReportPdfReady();
        if (!cancelled) {
          markReportPdfReady();
          setPdfReadySignaled(true);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Unable to prepare PDF report";
          markReportPdfError(message);
          setExportError(message);
        }
      }
    };

    signalReady();

    return () => {
      cancelled = true;
      setPdfReadySignaled(false);
      document.documentElement.removeAttribute("data-pdf-ready");
    };
  }, [isPdfExport, isReportReady, reportPdfModel]);

  return (
    <>
      {!isPdfExport && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
          {exportError && (
            <p className="max-w-xs rounded-lg bg-red-600 px-3 py-2 text-xs text-white shadow-lg">
              {exportError}
            </p>
          )}
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={!isReportReady || isExportingPdf}
            className="rounded-full bg-[#0057FF] px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-[#0046cc] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isExportingPdf ? "Generating PDF..." : "Download PDF"}
          </button>
        </div>
      )}

      {isPdfExport && !pdfReadySignaled && (
        <div className="fixed inset-x-0 top-0 z-50 bg-[#0057FF] px-4 py-2 text-center text-sm font-medium text-white">
          Preparing PDF report...
        </div>
      )}

      {isReportReady && (
        <PdfExport model={reportPdfModel} visible={isPdfExport} />
      )}

      {!isPdfExport && (
      <main className="report-typography min-h-screen w-full">
      <Cover />
      <Snapshot
        overallScore={overallScore || 86}
        readiness={snapshotData.readiness}
        fastestRoiArea={snapshotData.fastestRoiArea}
        pilotReadiness={snapshotData.pilotReadiness}
        opportunityWindow={snapshotData.opportunityWindow}
        executivePhrase={snapshotData.executivePhrase}
        benchmarkCopy={snapshotData.benchmarkCopy}
        bulletSignals={snapshotData.bulletSignals}
      />
      <SignalBreakdown scores={breakdownScores} notes={breakdownNotes} />
      <Interpretation interpretationCopy={reportCopy?.executive_interpretation} />
      <Opportunities cards={opportunityCards} />
      <Matrix roiPrioritization={roiPrioritization} />
      <Roadmap phases={executionPlan} />
      <Outcomes expectedOutcomes={expectedOutcomes} />
      <Positioning industryPositioning={industryPositioning} />
      <Risks risks={strategicRisks} />
      <Framework />
      <NextNinety
        sectionEyebrow={recommendedImmediateActions?.section_eyebrow}
        sectionTitle={recommendedImmediateActions?.section_title}
        items={recommendedImmediateActions?.items}
      />
      <AboutGowitek />
      <SignalInputs assessmentSignalInputs={assessmentSignalInputs} />
      <Closing />
      </main>
      )}
    </>
  );
}

function inferReadinessLevel(score: number) {
  if (score >= 70) return "High";
  if (score >= 40) return "Medium";
  return "Low";
}

function getExecutivePhrase(readinessLevel: string) {
  switch (readinessLevel.toLowerCase()) {
    case "high":
      return "beyond AI exploration";
    case "medium":
      return "ready for focused AI pilots";
    default:
      return "at the foundation stage";
  }
}

function getBenchmarkCopy(score: number) {
  if (score >= 80) return "Top 12% of mid-market organizations assessed";
  if (score >= 60) return "Top 35% of mid-market organizations assessed";
  return "Ahead of the baseline curve for first-time assessments";
}

function getPilotReadiness(score: number) {
  if (score >= 80) return "90-Day Ready";
  if (score >= 60) return "3–6 Month Ready";
  return "Foundational Work First";
}

function getOpportunityWindow(score: number) {
  if (score >= 80) return "6–12 Months";
  if (score >= 60) return "3–6 Months";
  return "0–3 Months";
}

function normalizeExecutivePhrase(value: string) {
  return (value || "")
    .replace(/^your organization is\s+/i, "")
    .replace(/^organization is\s+/i, "")
    .replace(/\.+$/, "")
    .trim();
}

function buildSignalBullets(pillars: Array<[string, number]>) {
  const fallback = ["leadership alignment", "cloud readiness", "operational maturity", "AI execution openness"];

  if (!pillars.length) {
    return fallback;
  }

  return pillars.slice(0, 4).map(([pillar]) => {
    const label = PILLAR_LABELS[pillar] || pillar;
    return label.toLowerCase();
  });
}

function resolvePillarScore(
  pillarScores: Record<string, number>,
  aliases: string[],
  fallback: number,
) {
  for (const alias of aliases) {
    const value = pillarScores[alias];
    if (typeof value === "number" && Number.isFinite(value)) {
      return normalizeScore(value);
    }
  }
  return fallback;
}

function normalizeScore(value: number) {
  const normalized = value <= 1 ? value * 100 : value;
  return Math.max(0, Math.min(100, Math.round(normalized)));
}

/* ───────────────────────── nav ───────────────────────── */

/* ───────────────────────── 1. cover ───────────────────────── */

function Cover() {
  return (
    <section data-pdf-section="true" className="report-cover relative overflow-hidden bg-signal-soft">
      <div className="report-cover-inner mx-auto flex min-h-[92vh] max-w-6xl flex-col px-6 py-10">
        {/* top */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-dark">
              <Activity className="h-4 w-4 text-signal" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold tracking-tight text-foreground">Signal AI</span>
              <span className="inline-flex items-center gap-0">
                <span className="text-xs font-medium text-muted-foreground">by</span>
                <img src={bgLogo} alt="Gowitek" className="h-6 w-auto object-contain" />
              </span>
            </div>
          </div>
          <span className="chip">Confidential · Executive Brief</span>
        </div>

        {/* center */}
        <div className="report-cover-center relative my-auto flex flex-col items-center text-center">
          <div className="absolute inset-0 -z-10 flex items-center justify-center">
            <SignalMesh className="h-[560px] w-[860px] opacity-90" />
          </div>
          <span className="chip mb-6">Executive Report</span>
          <h1 className="report-h1 max-w-3xl">
            Executive <span className="text-gradient">AI Readiness</span><br />Assessment
          </h1>
          <p className="report-body mt-6 max-w-xl">
            Assessment of AI readiness, operational gaps, and opportunities for ROI improvement.
          </p>
        </div>

        {/* bottom */}
        <div className="report-cover-footer mt-12 grid grid-cols-1 md:grid-cols-3 items-center gap-6">
          <div className="flex items-center">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-dark">
                <Activity className="h-4 w-4 text-signal" strokeWidth={2.5} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold tracking-tight text-foreground">Signal AI</span>
                <span className="inline-flex items-center gap-0">
                  <span className="text-xs font-medium text-muted-foreground">by</span>
                  <img src={bgLogo} alt="Gowitek" className="h-6 w-auto object-contain" />
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <p className="report-caption text-foreground/55">
              Gowitek Signal AI · AI Readiness Assessment
            </p>
          </div>
          <div className="flex items-center justify-end" />
        </div>
      </div>
    </section>
  );
}


function Closing() {
  return (
    <section id="closing" data-pdf-section="true" className="report-closing relative overflow-hidden bg-navy-deep text-white">
      <SignalMesh className="absolute inset-0 h-full w-full opacity-30" />
      <div className="report-closing-inner relative mx-auto flex min-h-[90vh] max-w-4xl flex-col items-center justify-center px-6 py-32 text-center">
        <span className="chip chip-dark mb-8">Closing</span>
        <h2 className="report-h1">
          Focus on measurable<br />
          <span className="text-gradient">business outcomes.</span>
        </h2>
        <p className="report-body mt-8 max-w-xl text-white/70">
          This assessment identifies where AI can improve operational and financial performance before major investment.
        </p>

        <p className="report-closing-footer mt-6 text-[13px] text-white/55">
          connect@gowitek.com
        </p>
      </div>
    </section>
  );
}
